// EPOCH 35 - Phase 3: CSR Campaign Creation Page
// Allows CSRs to create campaigns on behalf of users

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';
import { useCSRContext } from '../../hooks/useCSRContext';
import { CSRUserSelector } from '../../components/csr/CSRUserSelector';
import { useCallback } from 'react';

const CSRCampaignCreate: React.FC = () => {
  const navigate = useNavigate();
  const { isCSR, targetUser } = useCSRContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Campaign form fields
  const [campaignName, setCampaignName] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [serviceTier, setServiceTier] = useState('junior_sdr');
  const [csrNotes, setCSRNotes] = useState('');

  const checkAuth = useCallback(async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleCreateCampaign = async () => {
    if (!targetUser) {
      setError('Please select a user to create a campaign for');
      return;
    }

    if (!campaignName.trim()) {
      setError('Please enter a campaign name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the RPC function to create campaign on behalf of user
      const { error: rpcError } = await supabase.rpc('csr_create_campaign_for_user', {
        p_user_id: targetUser.id,
        p_name: campaignName,
        p_data: {
          target_audience: targetAudience,
          service_tier: serviceTier
        },
        p_csr_notes: csrNotes
      }).single();

      if (rpcError) throw rpcError;

      // Success! Navigate to campaign details or list
      alert(`Campaign created successfully for ${targetUser.full_name || targetUser.email}!`);
      navigate('/campaigns');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  if (!isCSR) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800">Access Denied</h2>
          <p className="text-red-600 mt-2">This page is only accessible to CSR users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Create Campaign on Behalf of User
        </h1>
        <p className="text-gray-600">
          As a CSR, you can create campaigns for users who need assistance.
        </p>
      </div>

      {/* User Selection */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Step 1: Select User</h2>
        <CSRUserSelector />
        {!targetUser && (
          <p className="mt-2 text-sm text-gray-500">
            Select a user from your assigned companies to create a campaign for them.
          </p>
        )}
      </div>

      {/* Campaign Form - Only show if user is selected */}
      {targetUser && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Step 2: Campaign Details</h2>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              {/* Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g., Q1 2025 Lead Generation Campaign"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g., CTOs, VPs of Engineering, Tech Leads"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Service Tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Tier
                </label>
                {/* EPOCH 63: Renamed "Junior SDR" to "Platform", Top Qualifier hidden */}
                <select
                  value={serviceTier}
                  onChange={(e) => setServiceTier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="junior_sdr">Platform</option>
                  {/* Top Qualifier hidden - may return in future */}
                </select>
              </div>

              {/* CSR Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CSR Notes
                  <span className="text-gray-500 ml-2 font-normal">
                    (Why are you creating this campaign?)
                  </span>
                </label>
                <textarea
                  value={csrNotes}
                  onChange={(e) => setCSRNotes(e.target.value)}
                  placeholder="e.g., User requested assistance during onboarding call. They need help targeting the healthcare industry..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* CSR Context Indicator */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">ℹ️</span>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900">Creating as CSR</h3>
                <p className="text-purple-700 text-sm mt-1">
                  This campaign will be owned by <strong>{targetUser.full_name || targetUser.email}</strong> for 
                  billing purposes, but will be marked as created by you for audit purposes.
                </p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateCampaign}
              disabled={loading || !campaignName.trim()}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                loading || !campaignName.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {loading ? 'Creating...' : 'Create Campaign for User'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSRCampaignCreate;